import { LoadingPage } from '@/components/ui/loading-page';
import { isActivePaidSubscription } from '@/components/subscription/plan-choice/plan-choice-offer';
import { useReconcileShopifyFreeApproval } from '@/hooks/actions/use-reconcile-shopify-free-approval';
import { useBillingContextQuery } from '@/hooks/queries/use-billing-context-query';
import { useCardSetupStatusQuery } from '@/hooks/queries/use-card-setup-status-query';
import { usePaidCheckoutStatusQuery } from '@/hooks/queries/use-paid-checkout-status-query';
import { useAuth } from '@/contexts/auth-context';
import { readBillingContext } from '@/libs/billing-access';
import { usePaywall } from '@/hooks/use-paywall';
import { BillingModalFrame } from './BillingModalFrame';
import { getMcpReturnTo } from '@/libs/mcp-return-to';
import { Button, Stack } from '@chakra-ui/react';
import { NoticePanel } from '@fluenceur/ui';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BillingExits,
  BillingOnboardingContent,
} from '@/pages/onboarding/billing/BillingOnboardingContent';

export const BillingModalHost = ({ onClose }: { onClose?: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { closePaywall, origin } = usePaywall();
  const close = useCallback(() => {
    if (onClose) onClose();
    else closePaywall();
  }, [onClose, closePaywall]);
  const { user, isLoading } = useAuth();
  const query = useBillingContextQuery();
  const [params] = useSearchParams();
  const reconcileShopify = useReconcileShopifyFreeApproval();
  const checkedReturn = useRef<string | null>(null);
  const freeApproval = query.data?.shopifyFreeApproval;
  const returningFromShopify = params.get('shopify_free_approval') === 'true';
  useEffect(() => {
    const operationId = freeApproval?.operationId;
    if (
      !returningFromShopify ||
      query.data?.channel !== 'shopify' ||
      query.data.brandId !== user?.currentBrandId ||
      query.data.organizationId !== user?.currentBrand?.organizationId ||
      !freeApproval?.canApprove ||
      !operationId ||
      freeApproval.requirement !== 'pending'
    )
      return;
    const target = `${user?.id}:${query.data.organizationId}:${query.data.brandId}:${operationId}`;
    if (checkedReturn.current === target) return;
    checkedReturn.current = target;
    void reconcileShopify.mutateAsync(operationId).catch(() => undefined);
  }, [
    freeApproval,
    returningFromShopify,
    query.data,
    user?.id,
    user?.currentBrandId,
    user?.currentBrand?.organizationId,
    reconcileShopify,
  ]);
  const paidIntent =
    ['essential', 'growth'].includes(params.get('plan') ?? '') || !!query.data?.paidCheckout;
  const status = useCardSetupStatusQuery({ context: query.data, enabled: !query.error });
  const returnTo = getMcpReturnTo(params.get('returnTo')) ?? getMcpReturnTo(origin);
  const paidStatus = usePaidCheckoutStatusQuery({
    context: query.data,
    enabled: !query.error,
    ...(returnTo ? { returnTo } : {}),
  });
  const refresh = () =>
    paidStatus.canReadOperation
      ? paidStatus.refetch()
      : !paidIntent && status.canReadOperation
        ? status.refetch()
        : query.refetch();
  const context = readBillingContext(query.data);
  const matchesScope =
    !!context &&
    context.brandId === user?.currentBrandId &&
    context.organizationId === user?.currentBrand?.organizationId;
  // Without access (no paid plan, no free plan, no bypass) billing is the only way
  // forward, so the modal cannot be dismissed; its exits stay available.
  const dismissible = !(matchesScope && !context.access.allowed);
  const isProviderReturn =
    returningFromShopify || params.has('operationId') || params.has('session_id');
  const completed =
    matchesScope &&
    !query.isFetching &&
    context.access.allowed &&
    context.cardSetup.requirement !== 'required' &&
    (!paidIntent || isActivePaidSubscription(context.subscription));
  useEffect(() => {
    if (!isProviderReturn || !completed || query.error || status.error || paidStatus.error) return;
    void closePaywall({ notify: false, history: 'replace' }).then(() => {
      if (paidIntent && returnTo) navigate(returnTo, { replace: true });
    });
  }, [
    isProviderReturn,
    completed,
    query.error,
    status.error,
    paidStatus.error,
    closePaywall,
    paidIntent,
    returnTo,
    navigate,
  ]);
  if (isLoading || query.isPending)
    return (
      <BillingModalFrame onClose={close}>
        <LoadingPage />
      </BillingModalFrame>
    );
  if (
    !user ||
    query.error ||
    (!paidIntent && status.error) ||
    paidStatus.error ||
    !context ||
    !matchesScope
  ) {
    return (
      <BillingModalFrame onClose={close}>
        <Stack gap={4}>
          <NoticePanel
            tone="warning"
            title={t(paidIntent ? 'planChoice.paid.readError' : 'onboarding.billing.readError')}
            actions={
              <Button variant="outline" onClick={() => void refresh()}>
                {t('common.retry')}
              </Button>
            }
          />
          {user && <BillingExits canReadBilling={false} />}
        </Stack>
      </BillingModalFrame>
    );
  }
  return (
    <BillingOnboardingContent
      key={`${query.data.organizationId}:${query.data.brandId}:${user.id}:${user.email}`}
      user={user}
      context={context}
      refresh={refresh}
      unavailable={query.isFetching}
      onClose={close}
      dismissible={dismissible}
    />
  );
};

export default BillingModalHost;
